/**
 * Guard de cambios sin guardar para las secciones editables del panel.
 *
 * Uso:
 *   const formDirty = useFormDirty(form)          // opcional: snapshot + comparación
 *   useUnsavedChangesGuard({ dirty, saveFn: save, message: '...' })
 *
 * Cuando el usuario intenta salir de la ruta con `dirty === true` se abre un
 * modal global (UnsavedChangesModal) con:
 *   - "Guardar y salir": corre `saveFn`, y si al terminar el formulario quedó
 *     limpio (markClean / dirty === false) navega al destino original.
 *   - "Salir sin guardar": navega sin guardar.
 *   - "Cancelar": se queda en la sección.
 *
 * También registra el aviso nativo del browser (beforeunload) al cerrar o
 * recargar la pestaña con cambios pendientes.
 */
import { ref, reactive, watch, onBeforeUnmount } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import router from '@/router'

// ── Estado compartido del modal de confirmación (singleton) ──────────
const modal = reactive({
  visible: false,
  message: '',
  saving: false,
  next: null,        // callback de navegación pendiente (vue-router)
  saveFn: null,      // función async de guardado de la página
  dirtyRef: null,    // ref dirty de la página (para saber si el save limpió)
  fromPath: null,    // fullPath de la ruta al momento de bloquear
})

function close() {
  modal.visible = false
  modal.next = null
  modal.saveFn = null
  modal.dirtyRef = null
  modal.fromPath = null
}

async function confirmSave() {
  const next = modal.next
  const fromPath = modal.fromPath
  const dirtyRef = modal.dirtyRef
  modal.next = null
  modal.saving = true
  try {
    if (modal.saveFn) await modal.saveFn()
  } catch (e) {
    // La página muestra su propio toast/error; nos quedamos en la sección.
    modal.saving = false
    close()
    return
  }
  modal.saving = false
  const saved = !dirtyRef?.value
  const navigated = !!fromPath && router.currentRoute.value.fullPath !== fromPath
  close()
  // Si el saveFn ya navegó solo (ej: ProductForm al crear) o falló
  // silenciosamente (validación), no navegar de nuevo.
  if (next && saved && !navigated) next()
}

function discard() {
  const next = modal.next
  close()
  if (next) next()
}

function cancel() {
  close()
}

const DEFAULT_MESSAGE =
  'Tenés cambios sin guardar en esta sección. Si salís ahora, se van a perder.'

export function useUnsavedChangesGuard({ dirty, saveFn = null, message = DEFAULT_MESSAGE }) {
  onBeforeRouteLeave((to, from, next) => {
    if (modal.saving) return next() // ya estamos guardando: dejar pasar
    if (!dirty.value) return next()
    modal.message = message
    modal.saveFn = saveFn || null
    modal.dirtyRef = dirty
    modal.fromPath = from.fullPath
    modal.next = next
    modal.visible = true
  })

  // Aviso nativo del browser al cerrar/recargar la pestaña.
  const onBeforeUnload = (e) => {
    if (dirty.value && !modal.saving) {
      e.preventDefault()
      e.returnValue = ''
    }
  }
  watch(
    dirty,
    (d) => {
      if (d) window.addEventListener('beforeunload', onBeforeUnload)
      else window.removeEventListener('beforeunload', onBeforeUnload)
    },
    { immediate: true }
  )
  onBeforeUnmount(() => window.removeEventListener('beforeunload', onBeforeUnload))
}

// ── Helpers de comparación (clave ordenada + undefined→null) ─────────
function normalize(v) {
  if (v === undefined) return null
  if (Array.isArray(v)) return v.map(normalize)
  if (v && typeof v === 'object') {
    const out = {}
    for (const k of Object.keys(v).sort()) out[k] = normalize(v[k])
    return out
  }
  return v
}

function deepEqual(a, b) {
  return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b))
}

function clone(v) {
  return v === undefined ? undefined : JSON.parse(JSON.stringify(v))
}

/**
 * Dirty por snapshot: compara el formulario contra una foto del estado
 * cargado. `markClean()` se llama después de cargar y después de guardar.
 * Si el usuario revierte los cambios a mano, vuelve a quedar limpio.
 */
export function useFormDirty(source, { initial = null } = {}) {
  const dirty = ref(false)
  let snapshot = initial !== null ? clone(initial) : null
  let armed = initial !== null

  function markClean() {
    snapshot = clone(source.value !== undefined ? source.value : source)
    armed = true
    dirty.value = false
  }

  function reset() {
    armed = false
    snapshot = null
    dirty.value = false
  }

  watch(
    source,
    (val) => {
      if (!armed || snapshot === null) return
      dirty.value = !deepEqual(snapshot, val)
    },
    { deep: true }
  )

  return { dirty, markClean, reset }
}

export { modal, confirmSave, discard, cancel }
